import { computed, ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { formatDayMonthDate } from '@/lib/utils'

import { usePlanningStore } from '@/stores/planning.store'
import { entityChangeTypeEnum, usePortalStore } from '@/stores/portal.store'
import { useScheduleStore, taskDueBucketEnum } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { signalToneEnum, signalToneColor } from '@/composables/dashboard-kpi'
import { extraValueEntries } from '@/composables/extra-fields'
import { useNotify } from '@/composables/notify'
import { useEntityChanges } from '@/composables/entity-changes'

const GENERIC_TYPE_KEY = 'generic'
const WITHOUT_PROJECT_KEY = 'personal'

export const isTaskOverdue = (task) => !task.completed && task.due_bucket === taskDueBucketEnum.OVERDUE

export const taskDueLabel = (task) => {
  if (!task.due_date) return null
  const date = formatDayMonthDate(task.due_date)
  return task.due_time ? `${date} ${task.due_time.slice(0, 5)}` : date
}

export const taskDueColor = (task) => (isTaskOverdue(task) ? signalToneColor(signalToneEnum.ATTENTION) : undefined)

export const taskDueClass = (task) => (isTaskOverdue(task) ? `text-${taskDueColor(task)}` : undefined)

export const taskContainerLabel = (task) => task.project_name ?? getI18n().t('common.tasks.withoutProject')

export const countTaskProgress = (tasks) => ({ total: tasks.length, done: tasks.filter(task => task.completed).length })

export const countTaskWarnings = (tasks) => {
  const open = tasks.filter(task => !task.completed)
  return {
    overdue: open.filter(isTaskOverdue).length,
    missingServices: open.filter(task => task.missing_service_ids?.length).length,
  }
}

export const useTaskMeta = () => {
  const scheduleStore = useScheduleStore()

  const extraValueChips = (task) => extraValueEntries(scheduleStore.getTaskTypeById(task.task_type)?.extra_fields, task.extra_values)
    .map(({ field, label }) => ({ key: `extra-${field.id}`, label, tooltip: field.name }))

  return { extraValueChips }
}

export const useTaskGroups = () => {
  const scheduleStore = useScheduleStore()

  const typeSections = (tasks) => {
    const typeIds = [...new Set(tasks.map(task => task.task_type ?? null))]
    const typeOrder = (typeId) => (typeId === null ? -1 : scheduleStore.getTaskTypeById(typeId)?.order ?? 0)
    return typeIds
      .sort((a, b) => typeOrder(a) - typeOrder(b))
      .map(typeId => ({
        key: typeId ?? GENERIC_TYPE_KEY,
        taskType: typeId === null ? null : scheduleStore.getTaskTypeById(typeId),
        tasks: tasks.filter(task => (task.task_type ?? null) === typeId),
      }))
  }

  const groupByProject = (tasks, splitByType) => {
    const projectIds = [...new Set(tasks.map(task => task.project ?? null))]
    return projectIds
      .map((projectId) => {
        const projectTasks = tasks.filter(task => (task.project ?? null) === projectId)
        return {
          key: projectId ?? WITHOUT_PROJECT_KEY,
          projectId,
          label: taskContainerLabel(projectTasks[0]),
          color: projectTasks[0].project_color,
          progress: countTaskProgress(projectTasks),
          warnings: countTaskWarnings(projectTasks),
          sections: splitByType ? typeSections(projectTasks) : [{ key: 'all', taskType: undefined, tasks: projectTasks }],
        }
      })
      .sort((a, b) => (a.projectId === null) - (b.projectId === null) || a.label.localeCompare(b.label))
  }

  return { groupByProject }
}

export const useNewTaskDefaults = () => {
  const webSiteStore = useWebSiteStore()
  return (projectId = null, taskTypeId = null) => ({
    project: projectId,
    task_type: taskTypeId,
    assignees: webSiteStore.user?.service_id ? [webSiteStore.user.service_id] : [],
    services: [],
    extra_values: {},
  })
}

export const useMyTaskFilter = () => {
  const webSiteStore = useWebSiteStore()
  return (task) => (task.assignees ?? []).includes(webSiteStore.user?.service_id) || (!task.project && task.created_by === webSiteStore.user?.id)
}

export const useTaskActions = () => {
  const scheduleStore = useScheduleStore()
  const newTaskDefaults = useNewTaskDefaults()

  const { notifySuccess } = useNotify()

  const toggleCompletedAsync = async(task) => {
    const completed = !task.completed
    task.completed = completed
    try {
      await scheduleStore.setTaskCompletedAsync(task, completed)
    } catch (err) {
      task.completed = !completed
      throw err
    }
  }

  const deleteAsync = async(taskId) => {
    await scheduleStore.deleteTaskAsync(taskId)
    notifySuccess(getI18n().t('info.deletedSuccessfully'))
  }

  return { toggleCompletedAsync, deleteAsync, newTaskDefaults }
}

export const useTaskChanges = (onChangedAsync) => {
  const portalStore = usePortalStore()
  const scheduleStore = useScheduleStore()
  watch(() => [scheduleStore.taskChangesKey, portalStore.reloadKey], () => onChangedAsync())
  useEntityChanges([entityChangeTypeEnum.TASK], () => onChangedAsync())
}

export const useTaskLoader = (fetchAsync, { onNewTaskAsync = null, enabled = () => true } = {}) => {
  const tasks = ref([])
  let loaded = false

  const loadAsync = async(highlightNew = true) => {
    const knownIds = new Set(tasks.value.map(task => task.id))
    tasks.value = await fetchAsync()
    const newId = highlightNew && loaded ? tasks.value.find(task => !knownIds.has(task.id))?.id : null
    loaded = true
    if (newId) await onNewTaskAsync?.(newId)
  }

  useTaskChanges(() => enabled() && loadAsync())

  return { tasks, loadAsync }
}

export const useTaskServiceOptions = () => {
  const planningStore = usePlanningStore()
  const named = (services) => services.filter(service => !service.archived).map(service => ({ ...service, name: planningStore.getServiceName(service) }))
  const staffOptions = computed(() => named(planningStore.staff))
  const resourceOptions = computed(() => named([...planningStore.equipments, ...planningStore.groups]))

  const ensureServicesAsync = () => Promise.all([
    planningStore.staff.length ? null : planningStore.fetchStaffsAsync({ all: true }, true),
    planningStore.equipments.length ? null : planningStore.fetchEquipmentsAsync({ all: true }, true),
    planningStore.groups.length ? null : planningStore.fetchGroupsAsync({ all: true }, true),
  ])

  return { staffOptions, resourceOptions, ensureServicesAsync }
}

export const projectMissingServiceIds = (tasks) => [...new Set(tasks.flatMap(task => task.missing_service_ids ?? []))]
